import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withContentRect } from 'react-measure';
import { throttle } from 'lodash';
import renderIf from 'render-if';
import classnames from 'classnames';
import {
  dateAdd,
  dateDiff,
  dateSubtract,
  dateMax,
  dateMin,
} from 'javascripts/util/dateFns';
import { clampDate, getPixelsPerDay, onScroll } from './helpers';
import { instanceOfDateString } from './propTypes';
import SliderBar from './SliderBar';
import ScaleDates from './ScaleDates';
import styles from './ScaleBar.module.css';
import { FISCAL_YEAR_FORMATS } from 'javascripts/util/fiscalFormats';

// The width of the draggable bar is 30px less than the width of the entire
// component, because it has 15px margin on both sides.
const BAR_PADDING = 30;

/*
 * Top level ScaleBar component. It accepts `startDate` and `endDate` props
 * to define the full width of the scale, and `visibleStartDate` and
 * `visibleEndDate` to define the currently visible segment of the scale.
 */
class ScaleBar extends Component {
  static propTypes = {
    measureRef: PropTypes.func.isRequired,
    contentRect: PropTypes.shape({
      offset: PropTypes.shape({
        width: PropTypes.number,
      }).isRequired,
    }).isRequired,
    startDate: instanceOfDateString.isRequired,
    endDate: instanceOfDateString.isRequired,
    visibleStartDate: instanceOfDateString.isRequired,
    visibleEndDate: instanceOfDateString.isRequired,
    onSetVisibleDates: PropTypes.func.isRequired,
    publishing: PropTypes.bool.isRequired,
    hideWeekends: PropTypes.bool.isRequired,
    fiscalMonth: PropTypes.number.isRequired,
    fiscalYearFormat: PropTypes.string,
    timescale: PropTypes.string.isRequired,
  };

  static defaultProps = {
    publishing: false,
    hideWeekends: false,
    fiscalMonth: 1,
    fiscalYearFormat: FISCAL_YEAR_FORMATS.SINGLE_YEAR_QUARTER_LAST,
    timescale: 'auto',
  };

  constructor(props) {
    super(props);

    // 60FPS = 1 update every 16.67 milliseconds. Anything
    // more frequent is just wasted CPU.
    this.onBarMove = throttle(this.onBarMove, 16);
    this.onScroll = throttle(this.onScroll, 16);
    this.onHandleMove = throttle(this.onHandleMove, 16);
  }

  onStartDrag = () => {
    this.visibleStartDateAtStartOfDrag = this.props.visibleStartDate;
    this.visibleEndDateAtStartOfDrag = this.props.visibleEndDate;
  };

  onBarMove = distance => {
    const {
      startDate,
      endDate,
      hideWeekends,
      contentRect: {
        offset: { width },
      },
    } = this.props;
    const barWidth = width - BAR_PADDING;
    const pixelsPerDay = getPixelsPerDay({
      startDate,
      endDate,
      hideWeekends,
      width: barWidth,
    });
    const numDays = distance / pixelsPerDay;

    let newVisibleStartDate = dateAdd(
      this.visibleStartDateAtStartOfDrag,
      numDays,
      { business: hideWeekends }
    );
    let newVisibleEndDate = dateAdd(this.visibleEndDateAtStartOfDrag, numDays, {
      business: hideWeekends,
    });

    const startDiff = dateDiff(startDate, newVisibleStartDate, {
      business: hideWeekends,
    });
    if (startDiff < 0) {
      newVisibleStartDate = dateSubtract(newVisibleStartDate, startDiff, {
        business: hideWeekends,
      });
      newVisibleEndDate = dateSubtract(newVisibleEndDate, startDiff, {
        business: hideWeekends,
      });
    }

    const endDiff = dateDiff(endDate, newVisibleEndDate, {
      business: hideWeekends,
    });
    if (endDiff > 0) {
      newVisibleStartDate = dateSubtract(newVisibleStartDate, endDiff, {
        business: hideWeekends,
      });
      newVisibleEndDate = dateSubtract(newVisibleEndDate, endDiff, {
        business: hideWeekends,
      });
    }

    this.onSetVisibleDates(newVisibleStartDate, newVisibleEndDate);
  };

  onHandleMove = (location, distance) => {
    const {
      startDate,
      endDate,
      hideWeekends,
      contentRect: {
        offset: { width },
      },
    } = this.props;

    const barWidth = width - BAR_PADDING;

    const pixelsPerDay = getPixelsPerDay({
      startDate,
      endDate,
      hideWeekends,
      width: barWidth,
    });

    const numDays = distance / pixelsPerDay;

    if (location === 'left') {
      this.onSetVisibleDates(
        dateMax(
          startDate,
          dateMin(
            dateAdd(this.visibleStartDateAtStartOfDrag, numDays, {
              business: hideWeekends,
            }),
            dateSubtract(this.visibleEndDateAtStartOfDrag, 7, {
              business: hideWeekends,
            })
          )
        ),
        this.visibleEndDateAtStartOfDrag
      );
    } else {
      this.onSetVisibleDates(
        this.visibleStartDateAtStartOfDrag,
        dateMin(
          endDate,
          dateMax(
            dateAdd(this.visibleEndDateAtStartOfDrag, numDays, {
              business: hideWeekends,
            }),
            dateAdd(this.visibleStartDateAtStartOfDrag, 7, {
              business: hideWeekends,
            })
          )
        )
      );
    }
  };

  onScroll = offset => {
    const {
      contentRect: {
        offset: { width },
      },
      startDate,
      endDate,
      visibleStartDate,
      visibleEndDate,
      hideWeekends,
    } = this.props;

    onScroll(offset, {
      endDate,
      hideWeekends,
      onSetVisibleDatesHandler: this.onSetVisibleDates,
      startDate,
      visibleEndDate,
      visibleStartDate,
      width,
    });
  };

  onSetVisibleDates = (visibleStartDate, visibleEndDate) => {
    requestAnimationFrame(() => {
      this.props.onSetVisibleDates(visibleStartDate, visibleEndDate);
    });
  };

  render() {
    // Normalize and validate the data -- ensure the total range is no more than
    // 20 years past/future, and the visible range is contained within the total range.
    const startDate = clampDate(this.props.startDate);
    const endDate = clampDate(this.props.endDate);
    const visibleStartDate = dateMax(startDate, this.props.visibleStartDate);
    const visibleEndDate = dateMin(endDate, this.props.visibleEndDate);

    const {
      measureRef,
      contentRect: {
        offset: { width },
      },
      fiscalMonth,
      fiscalYearFormat,
      hideWeekends,
      publishing,
      timescale,
    } = this.props;

    if (!width) {
      return <div ref={measureRef} className={styles.scaleBar} />;
    }

    const barWidth = width - BAR_PADDING;

    return (
      <div
        ref={measureRef}
        aria-label='scale bar'
        className={classnames(
          styles.scaleBar,
          publishing && styles.scaleBarPublishing
        )}
      >
        {renderIf(!publishing)(() => (
          <div className={styles.scaleSlider}>
            <div className={styles.scaleTrack}>
              <SliderBar
                startDate={startDate}
                endDate={endDate}
                visibleStartDate={visibleStartDate}
                visibleEndDate={visibleEndDate}
                width={barWidth}
                onStartDrag={this.onStartDrag}
                onBarMove={this.onBarMove}
                onHandleMove={this.onHandleMove}
              />
            </div>
          </div>
        ))}

        <ScaleDates
          startDate={startDate}
          endDate={endDate}
          visibleStartDate={visibleStartDate}
          visibleEndDate={visibleEndDate}
          visibleWidth={width}
          fiscalMonth={fiscalMonth}
          fiscalYearFormat={fiscalYearFormat}
          hideWeekends={hideWeekends}
          timescale={timescale}
          onScroll={this.onScroll}
        />
      </div>
    );
  }
}

export default withContentRect('offset')(ScaleBar);
